'use client';

import React, { useEffect, useState } from 'react';
import { RiArrowDownSLine, RiHome4Line, RiRefreshLine } from 'react-icons/ri';
import ThemedButton from '@/components/common/buttons/Button';
import ErrorLayout from '@/components/common/errors/ErrorLayout';
import ErrorLogo from '@/components/common/errors/ErrorLogo';
import ErrorMark from '@/components/common/errors/ErrorMark';
import GoBackButton from '@/components/common/errors/GoBackButton';

const IS_DEV = process.env.NODE_ENV !== 'production';

export default function AppError({
  error,
  reset,
}: {
  error: Error & { digest?: string };
  reset: () => void;
}) {
  const [detailsOpen, setDetailsOpen] = useState(false);

  useEffect(() => {
    console.error('[error boundary]', error.digest ?? error.message, error);
  }, [error]);

  return (
    <ErrorLayout
      status={500}
      eyebrow="500 error"
      title="Something went wrong"
      description={
        <>
          <p className="max-w-md text-lg leading-[1.6] text-[#5A5E67] sm:text-xl">
            We encountered an unexpected error. Please try again.
          </p>
          <p className="max-w-md text-sm leading-relaxed text-[#9CA3AF]">
            If the issue persists, contact support for assistance.
          </p>
          {error.digest && (
            <p className="font-mono text-xs text-[#9CA3AF]">
              Reference: {error.digest}
            </p>
          )}
        </>
      }
      logo={<ErrorLogo />}
      centerContent={<ErrorMark />}
      actions={
        <>
          <GoBackButton />
          <ThemedButton
            text="Try again"
            icon={<RiRefreshLine />}
            onClick={reset}
          />
          <ThemedButton
            variant="outline"
            text="Go home"
            icon={<RiHome4Line />}
            href="/"
          />
        </>
      }
    >
      {IS_DEV && (
        <div className="mt-4 w-full max-w-2xl">
          <button
            type="button"
            onClick={() => setDetailsOpen((o) => !o)}
            aria-expanded={detailsOpen}
            className="inline-flex items-center gap-2 text-sm font-medium text-[#5A5E67] hover:text-[#1B1B1B]"
          >
            <RiArrowDownSLine
              className={`transition-transform ${detailsOpen ? 'rotate-180' : ''}`}
            />
            {detailsOpen ? 'Hide error details' : 'Show error details (dev only)'}
          </button>
          {detailsOpen && (
            <div className="mt-3 max-h-[320px] overflow-auto border border-[#E9EAEB] bg-[#FAFAFA] p-4 font-mono text-xs">
              <div className="mb-2">
                <span className="font-bold">message:</span>{' '}
                {error.message || '(no message)'}
              </div>
              {error.digest && (
                <div className="mb-2">
                  <span className="font-bold">digest:</span> {error.digest}
                </div>
              )}
              {error.stack && (
                <pre className="leading-relaxed break-words whitespace-pre-wrap opacity-80">
                  {error.stack}
                </pre>
              )}
            </div>
          )}
        </div>
      )}
    </ErrorLayout>
  );
}
